import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
} from "lucide-react";

import { footerLinks, siteConfig } from "../../../data/siteConfig";
import { telegramLink, whatsappLink } from "../../../utils/contact";

const footerLinkClass =
  "inline-flex rounded-md text-sm font-medium text-zinc-600 transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-zinc-400 dark:hover:text-primary-300";

const socialLinkClass =
  "flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 transition-colors hover:border-primary-500 hover:bg-primary-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-primary-500 dark:hover:bg-primary-500 dark:hover:text-white";

const FooterComponent: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-zinc-200 bg-[#fffaf6] text-zinc-950 dark:border-zinc-800 dark:bg-zinc-950 dark:text-white">
      {/* Nom géant en arrière-plan */}
      <div className="absolute inset-x-0 flex justify-center overflow-hidden pointer-events-none top-10" aria-hidden="true">
        <span className="select-none whitespace-nowrap text-[18vw] font-black leading-none tracking-[-0.075em] text-primary-950/[0.035] dark:text-white/[0.025] sm:text-[13vw]">
          ONEPLACEDEAL
        </span>
      </div>

      {/* Décorations discrètes */}
      <div className="absolute bottom-0 rounded-full pointer-events-none -left-32 h-72 w-72 bg-primary-200/35 blur-3xl dark:bg-primary-900/10" />
      <div className="absolute rounded-full pointer-events-none -right-32 top-10 h-72 w-72 bg-gold-200/40 blur-3xl dark:bg-gold-900/10" />

      <div className="relative w-full px-4 pt-20 pb-8 mx-auto max-w-7xl sm:px-6 sm:pt-24 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-[1.45fr_0.75fr_0.75fr_1.1fr] lg:gap-10">
          {/* Marque */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" aria-label={`Accueil — ${siteConfig.name}`} className="inline-flex rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <img src="/images/logo/fulllogo.png" alt={siteConfig.name} loading="lazy" decoding="async" className="h-16 w-auto max-w-[220px] object-contain" />
            </Link>

            <p className="max-w-sm mt-5 text-sm font-medium leading-7 text-zinc-600 dark:text-zinc-400">
              {siteConfig.shortDescription}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-6" aria-label="Réseaux sociaux">
              <a href={whatsappLink(siteConfig.whatsappNumber, "Bonjour, j’ai une question concernant OnePlaceDeal.")} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 text-white transition-colors rounded-full border-emerald-200 bg-emerald-600 text-emerald-700 hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-emerald-900 dark:bg-emerald-600 dark:text-white dark:hover:bg-emerald-600 dark:hover:text-white" aria-label="Nous contacter sur WhatsApp">
                <MessageCircle size={17} />
              </a>

              <a href={telegramLink(siteConfig.telegramHandle)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 text-white transition-colors bg-blue-400 rounded-full">
                <Send size={17} />
              </a>

              <a href={siteConfig.socials.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 text-white transition-colors bg-blue-600 rounded-full" aria-label="Facebook">
                <Facebook size={17} fill="white"/>
              </a>

              <a href={siteConfig.socials.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 text-white transition-colors bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-full" aria-label="Instagram">
                <Instagram size={17} />
              </a>

            
            </div>
          </div>

          {/* Plateforme */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-primary-500" />

              <h2 className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-950 dark:text-white">
                Plateforme
              </h2>
            </div>

            <ul className="space-y-3">
              {footerLinks.plateforme.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Entreprise */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-gold-500" />

              <h2 className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-950 dark:text-white">
                Entreprise
              </h2>
            </div>

            <ul className="space-y-3">
              {footerLinks.entreprise.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-primary-500" />

              <h2 className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-950 dark:text-white">
                Contact
              </h2>
            </div>

            <ul className="space-y-4 text-sm font-medium text-zinc-600 dark:text-zinc-400">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                  <MapPin size={16} />
                </span>

                <span className="pt-1.5 leading-5">
                  {siteConfig.address}
                </span>
              </li>

              <li>
                <a href={`tel:${siteConfig.supportPhone.replace(/\s/g, "")}`} className="flex items-center gap-3 transition-colors rounded-lg hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300">
                  <span className="flex items-center justify-center h-9 w-9 shrink-0 rounded-xl bg-gold-100 text-gold-700 dark:bg-gold-900/30 dark:text-gold-300">
                    <Phone size={16} />
                  </span>

                  <span>{siteConfig.supportPhone}</span>
                </a>
              </li>

              <li>
                <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-3 transition-colors rounded-lg hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300">
                  <span className="flex items-center justify-center h-9 w-9 shrink-0 rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                    <Mail size={16} />
                  </span>

                  <span className="break-all">
                    {siteConfig.email}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bas du footer */}
        <div className="flex flex-col gap-5 border-t mt-14 border-zinc-200 pt-7 dark:border-zinc-800 md:flex-row md:items-center md:justify-between">
          <p className="text-xs font-medium text-zinc-500 dark:text-zinc-500">
            © {currentYear} {siteConfig.legalEntity}. Tous droits réservés.
          </p>

          <div className="flex flex-wrap gap-x-5 gap-y-3">
            {footerLinks.legal.map((link) => (
              <Link key={link.to} to={link.to} className="text-xs font-medium transition-colors rounded text-zinc-500 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterComponent;